// How to read the board: what it shows, where the data comes from, and what it never does.

import { Banner, Card } from '../components/Layout.tsx';
import { AlertIcon, ClockIcon, DatabaseIcon, InfoIcon, ShieldIcon } from '../icons.tsx';

export function HelpPage() {
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="title-row">
            <span className="phase">GUIDE</span>
            <h1>HOW TO READ THIS BOARD</h1>
          </div>
          <p className="subtitle">
            What every part of the board means, and where its data comes from.
          </p>
        </div>
      </div>
      <Banner>
        This is a Phase 1 prototype with synthetic patients only. It supports situational awareness;
        clinical judgement remains required.
      </Banner>
      <div className="grid-2">
        <Card title="The ED 4-hour standard" icon={<ClockIcon />}>
          <p>
            The NHS standard runs from <strong>arrival</strong> until the patient{' '}
            <strong>leaves the ED</strong>: moved to a ward, transferred or discharged. A decision
            to admit does not stop it.
          </p>
          <ul className="legend">
            <li>
              <span className="status status-none">
                <ClockIcon size={16} /> On track
              </span>{' '}
              under 3 hours
            </li>
            <li>
              <span className="status status-warning">
                <AlertIcon size={16} /> Warning
              </span>{' '}
              3 hours or more
            </li>
            <li>
              <span className="status status-breach">
                <AlertIcon size={16} /> Breached
              </span>{' '}
              4 hours or more
            </li>
          </ul>
          <p className="muted">
            Warnings and breaches are recorded by the Diagnuvo rules engine from clinical times. The
            bar under each patient fills towards 4 hours; the line marks 3 hours.
          </p>
        </Card>
        <Card title="Where the data comes from" icon={<DatabaseIcon />}>
          <ul className="plain">
            <li>
              <strong>Arrivals, moves, admissions, discharges:</strong> the patient administration
              system (PAS).
            </li>
            <li>
              <strong>Labs:</strong> the laboratory system (LIMS). <strong>Imaging:</strong>{' '}
              radiology (RIS) and the image archive (PACS).
            </li>
            <li>
              <strong>Observations:</strong> the e-obs system, shown exactly as sent. A NEWS2 score
              appears only if that system sends one.
            </li>
            <li>
              <strong>Times:</strong> when it happened (clinical time), never when the message
              arrived.
            </li>
          </ul>
        </Card>
        <Card title="What the board never does" icon={<ShieldIcon />}>
          <ul className="plain">
            <li>No risk scores, deterioration or sepsis predictions, or priority scores.</li>
            <li>No diagnosis, triage or treatment suggestions.</li>
            <li>No recalculation of scores sent by other systems.</li>
          </ul>
          <p className="muted">
            These are medical-device features. They need a clinical safety case and regulatory
            approval first.
          </p>
        </Card>
        <Card title="Privacy and access" icon={<InfoIcon />}>
          <ul className="plain">
            <li>Only invited staff in an ED role and a site group can see patients.</li>
            <li>Every view of the board and of a patient is recorded in an audit log.</li>
            <li>Sign out on shared computers.</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
